// Welcome to ServiceNow - Part 1: Course Overview through Tasks
// Topics: Course Overview, Platform Overview, Next Experience, Lists & Filters, Forms, Tasks

export const welcomePart1Topics = [
  {
    id: 'w-overview',
    title: 'Course Overview',
    readingTime: 8,
    subtopics: [
      {
        id: 'w-overview-intro',
        title: 'Welcome to ServiceNow Course Overview',
        readingTime: 8,
        content: `<h2>Welcome to Your ServiceNow Journey</h2>
<p>This comprehensive course is designed to provide you with an in-depth understanding of the ServiceNow platform — one of the most widely adopted enterprise cloud platforms in the world. Whether you are a new administrator stepping into the role, a developer looking to understand the platform's capabilities, or an IT professional seeking to expand your skillset, this training program will equip you with the knowledge and hands-on experience needed to succeed in your ServiceNow career.</p>

<h3>About This Training Program</h3>
<p>This eLearning module is structured into two major sections that progressively build your ServiceNow expertise:</p>
<ul>
<li><strong>Welcome to ServiceNow:</strong> This introductory section covers the foundational concepts of the ServiceNow platform. You will explore the user interface, learn how to navigate lists and forms, understand task management, work with analytics, knowledge management, the service catalog, and virtual agents. This section is designed to give you a comprehensive overview before diving into advanced administration topics.</li>
<li><strong>ServiceNow Administration Fundamentals (SNAF):</strong> This advanced section consists of 9 detailed modules covering everything from the modernized work experience and platform power to data management, self-service configuration, productivity tools, update set management, security, and a capstone project that ties everything together.</li>
</ul>

<h3>What You Will Learn</h3>
<p>By the end of this training program, you will be able to:</p>
<ul>
<li><strong>Navigate with Confidence:</strong> Master the Next Experience user interface, including workspaces, the All menu, favorites, history, and global search. You will understand how different user personas interact with the platform and how to customize their experience.</li>
<li><strong>Manage Data Effectively:</strong> Work with lists, filters, and forms to view, create, and modify records. Understand the table architecture, field types, and how data flows through the platform. Learn to import data from external sources and manage the Configuration Management Database (CMDB).</li>
<li><strong>Configure Business Applications:</strong> Set up and manage knowledge bases, service catalog items, virtual agents, notifications, and workflows. Understand how to tailor ServiceNow to meet specific business requirements through configuration rather than customization.</li>
<li><strong>Automate Processes:</strong> Use Flow Designer and workflow automation to streamline business processes, approvals, notifications, and task routing. Understand when to use different automation tools and how to design efficient workflows.</li>
<li><strong>Ensure Security:</strong> Implement role-based access control (RBAC), configure Access Control Lists (ACLs), and understand the shared responsibility model for ServiceNow security. Learn to use the Security Center to monitor and improve your instance's security posture.</li>
<li><strong>Manage Changes:</strong> Package configurations into update sets, move changes between instances following proper change management practices, and use ServiceNow utilities for troubleshooting and maintenance.</li>
</ul>

<h3>Prerequisites</h3>
<p>No prior ServiceNow experience is required to begin this course. However, the following background knowledge will enhance your learning experience:</p>
<ul>
<li><strong>Basic IT Concepts:</strong> Familiarity with IT Service Management (ITSM) terminology such as incidents, problems, changes, and service requests</li>
<li><strong>Web Browser Proficiency:</strong> Comfort with using modern web browsers, as ServiceNow is entirely web-based</li>
<li><strong>Logical Thinking:</strong> Ability to understand structured processes, conditional logic, and data relationships</li>
</ul>
<p>You will need access to a <strong>ServiceNow Personal Developer Instance (PDI)</strong> for hands-on exercises. PDIs are free and can be requested at <strong>developer.servicenow.com</strong>. Each PDI is a fully functional ServiceNow instance that you can use for learning, testing, and experimentation.</p>

<h3>How to Use This eLearning Module</h3>
<p>This module is designed to be self-paced. You can work through the content sequentially for the best learning experience, or jump to specific topics using the sidebar navigation and search functionality. Each section includes a reading time estimate to help you plan your study sessions. Use the bookmark feature to save important sections for quick reference later. Knowledge check quizzes at the end of each module help reinforce your learning — aim for at least 70% to demonstrate comprehension before moving on.</p>

<h3>Course Duration</h3>
<p>The complete training program is estimated to take approximately 40-60 hours to complete, including hands-on practice. The "Welcome to ServiceNow" section takes approximately 15-20 hours, while the SNAF modules require 25-40 hours depending on your pace and depth of practice.</p>`
      }
    ]
  },
  {
    id: 'w-platform',
    title: 'A ServiceNow Platform Overview',
    readingTime: 12,
    subtopics: [
      {
        id: 'w-platform-overview',
        title: 'ServiceNow Platform Overview',
        readingTime: 12,
        content: `<h2>The ServiceNow Platform</h2>
<p>ServiceNow is a cloud-based platform that provides digital workflows to automate and streamline enterprise operations. Originally founded in 2004 by Fred Luddy as a cloud-based IT service management (ITSM) tool, ServiceNow has evolved into a comprehensive enterprise platform that extends far beyond IT, serving departments including HR, Customer Service, Security Operations, and more. Today, over 80% of Fortune 500 companies rely on ServiceNow to run their critical business processes.</p>

<h3>Platform Architecture</h3>
<p>ServiceNow operates on a <strong>single-instance, multi-tenant architecture</strong> hosted in the cloud. This means each customer receives their own dedicated application instance with its own database, providing complete data isolation and security. Unlike shared multi-tenant architectures, ServiceNow's approach ensures that one customer's data and processes never interfere with another's.</p>

<p>The platform architecture consists of several key layers:</p>
<ul>
<li><strong>Now Platform (Foundation):</strong> The foundational layer provides the relational database, workflow engine, application framework, and core services. Every application built on ServiceNow shares this common foundation, which ensures consistency in data handling, security, and user experience across all modules.</li>
<li><strong>Application Layer:</strong> Pre-built applications sit on top of the Now Platform. These include IT Service Management (ITSM), IT Operations Management (ITOM), IT Asset Management (ITAM), Customer Service Management (CSM), HR Service Delivery (HRSD), Security Operations (SecOps), and many more. Each application is deeply integrated with the platform and with other applications.</li>
<li><strong>Integration Layer:</strong> ServiceNow provides robust integration capabilities through REST APIs, SOAP APIs, MID Servers (for on-premise connectivity), IntegrationHub (for low-code integrations), and import/export tools. This layer enables ServiceNow to communicate with virtually any external system.</li>
<li><strong>User Interface Layer:</strong> The Next Experience UI framework provides modern, responsive web interfaces. This includes the platform UI (for administrators and agents), the Service Portal (for end users), mobile applications, and configurable workspaces.</li>
</ul>

<h3>The Now Platform Technology Stack</h3>
<p>Understanding the technology stack helps administrators make better decisions about configuration and customization:</p>
<ul>
<li><strong>Database:</strong> ServiceNow uses a proprietary relational database built on MariaDB. All data is stored in tables, and the platform provides a powerful abstraction layer (GlideRecord API) for data access.</li>
<li><strong>Server-Side Scripting:</strong> JavaScript (Mozilla Rhino engine) is used for all server-side scripting, including Business Rules, Script Includes, Scheduled Jobs, and REST API implementations.</li>
<li><strong>Client-Side Scripting:</strong> Standard JavaScript runs in the browser for Client Scripts, UI Policies, and UI Actions that control form behavior.</li>
<li><strong>Workflow Engine:</strong> The platform includes a powerful workflow engine (now complemented by Flow Designer) that orchestrates multi-step business processes.</li>
</ul>

<h3>Key Platform Capabilities</h3>
<p>ServiceNow's capabilities are organized into four major workflow categories:</p>
<ul>
<li><strong>IT Workflows:</strong> Includes ITSM (Incident, Problem, Change Management), ITOM (Discovery, Event Management, Cloud Management), ITAM (Software, Hardware, Cloud Asset Management), DevOps, and Strategic Portfolio Management.</li>
<li><strong>Employee Workflows:</strong> HR Service Delivery, Workplace Service Delivery, Legal Service Delivery, and Employee Center — all designed to provide employees with consumer-grade service experiences.</li>
<li><strong>Customer Workflows:</strong> Customer Service Management (CSM), Field Service Management (FSM), and Order Management — helping organizations deliver exceptional customer experiences.</li>
<li><strong>Creator Workflows:</strong> App Engine (for rapid application development), IntegrationHub (for connecting systems), Flow Designer (for workflow automation), and the ServiceNow Store (marketplace for applications).</li>
</ul>

<h3>Instance Types and Release Cycle</h3>
<p>ServiceNow environments are organized into several types:</p>
<ul>
<li><strong>Production:</strong> The live instance used by all end users. Changes should never be made directly in production.</li>
<li><strong>Sub-Production:</strong> Development, Test, and Staging instances used for building, testing, and validating changes before production deployment.</li>
<li><strong>Personal Developer Instance (PDI):</strong> Free instances provided through developer.servicenow.com for individual learning and experimentation.</li>
</ul>
<p>ServiceNow follows a bi-annual major release cycle, with each release named alphabetically after a city. Recent releases include Tokyo, Utah, Vancouver, Washington DC, and Xanadu. Each release brings new features, performance improvements, and security enhancements. Customers typically have a 6-12 month window to upgrade to each new release.</p>

<h3>Why ServiceNow Matters</h3>
<p>ServiceNow's power lies in its ability to provide a <strong>single system of record</strong> for enterprise services. Instead of siloed tools for each department, ServiceNow provides a unified platform where workflows can span organizational boundaries. An employee onboarding process, for example, can automatically trigger IT provisioning, facilities setup, HR documentation, and security access — all within a single platform, with full visibility and accountability at every step.</p>`
      }
    ]
  },
  {
    id: 'w-next-exp',
    title: 'Next Experience, Navigation, and Access',
    readingTime: 25,
    subtopics: [
      {
        id: 'w-next-exp-lesson',
        title: 'Next Experience, Navigation, and Access Lesson',
        readingTime: 10,
        content: `<h2>Next Experience Interface</h2>
<p>Next Experience is ServiceNow's modern user interface framework, introduced as a significant evolution from the legacy UI16 interface. Built on the <strong>Seismic design system</strong>, Next Experience provides a more intuitive, responsive, and visually appealing experience that adapts to different user roles and workflows. Understanding this interface is the first skill every ServiceNow administrator must master.</p>

<h3>The Evolution from UI16 to Next Experience</h3>
<p>ServiceNow has gone through several UI generations. UI16 served as the primary interface for many years, featuring a left-side application navigator, a content frame, and a banner. While functional, UI16 had limitations in responsiveness, customization, and modern design patterns. Next Experience addresses these limitations with a completely redesigned interface built on modern web technologies.</p>

<p>Key differences from UI16 include:</p>
<ul>
<li><strong>Unified Navigation:</strong> The old left-side application navigator is replaced by the All menu — a centralized, searchable menu that organizes all applications and modules</li>
<li><strong>Workspace-Centric Design:</strong> Instead of navigating between separate modules, agents can work within configurable workspaces that combine dashboards, lists, records, and actions in a unified view</li>
<li><strong>Component-Based Architecture:</strong> The UI is built with reusable web components, enabling better performance, consistency, and customization</li>
<li><strong>Responsive Design:</strong> Next Experience works across desktop, tablet, and mobile devices without separate interfaces</li>
<li><strong>Polaris Theme:</strong> The default visual theme with modern typography, consistent spacing, improved color contrast, and a clean visual hierarchy</li>
</ul>

<h3>Key Interface Components</h3>
<p>The Next Experience interface is composed of several core components that administrators need to understand:</p>

<p><strong>1. Banner Frame (Top Navigation Bar)</strong></p>
<p>The banner frame is the persistent top bar that appears on every page. It contains:</p>
<ul>
<li><strong>All Menu:</strong> The primary navigation tool (replaces the Application Navigator). Click the hamburger icon or "All" text to open it. Type to search for any application, module, or record.</li>
<li><strong>Favorites:</strong> Star icon that provides quick access to bookmarked pages, modules, and records. You can organize favorites into groups.</li>
<li><strong>History:</strong> Clock icon showing your recently visited pages. Extremely useful for quickly returning to a page you were working on.</li>
<li><strong>Workspaces:</strong> Grid icon providing access to role-specific workspaces like ITSM Workspace, CSM Workspace, or HR Workspace.</li>
<li><strong>Global Search:</strong> Magnifying glass icon for searching records across all tables. Supports full-text search and can be configured to search specific tables.</li>
<li><strong>Connect Chat:</strong> Chat icon for real-time messaging between users. Supports direct messages and group conversations.</li>
<li><strong>Notifications:</strong> Bell icon showing system notifications, approval requests, and alerts.</li>
<li><strong>Help:</strong> Question mark icon accessing Instance Help, guided tours, and documentation.</li>
<li><strong>User Menu:</strong> Avatar/profile icon for accessing preferences, impersonation, elevate roles, and logout.</li>
</ul>

<p><strong>2. Content Frame</strong></p>
<p>The main area where lists, forms, dashboards, and other content are displayed. The content frame adapts based on what you're viewing — it could show a list of records, a single record form, a dashboard, or a workspace.</p>

<p><strong>3. Contextual Side Panels</strong></p>
<p>In workspaces, side panels can show related information, activity streams, or quick actions without navigating away from the current view.</p>

<h3>Navigation Techniques</h3>
<p>Efficient navigation is critical for administrator productivity. Here are the key navigation methods:</p>
<ul>
<li><strong>Filter Navigator (All Menu):</strong> Type the first few characters of a module name to instantly filter. For example, typing "inc" will show Incident-related modules. This is the fastest way to find any module.</li>
<li><strong>Direct URL Access:</strong> Navigate directly to any list by typing the table name followed by ".list" in the URL (e.g., <code>incident.list</code>). Navigate to a new record form with ".do" (e.g., <code>incident.do</code>).</li>
<li><strong>Keyboard Shortcuts:</strong> Press <code>Ctrl+Shift+F</code> to focus the global search. Use <code>Tab</code> and <code>Enter</code> to navigate the All menu without a mouse.</li>
<li><strong>Breadcrumbs:</strong> When in forms or nested pages, breadcrumbs at the top show your navigation path and allow you to jump back to previous levels.</li>
</ul>

<h3>Personalizing Your Experience</h3>
<p>Each user can personalize their interface through the User Menu > Preferences. Key personalization options include:</p>
<ul>
<li><strong>Date/Time Format:</strong> Choose your preferred date and time display format</li>
<li><strong>Time Zone:</strong> Critical for global organizations — ensures timestamps display in your local time</li>
<li><strong>Notifications:</strong> Configure which notifications you receive and how (email, in-platform, push)</li>
<li><strong>List Page Size:</strong> Set how many records appear per page in list views</li>
<li><strong>Accessibility:</strong> Enable high contrast mode, screen reader support, and other accessibility features</li>
</ul>`
      },
      {
        id: 'w-instance-help',
        title: 'Instance Help - Welcome to ServiceNow',
        readingTime: 8,
        content: `<h2>Instance Help</h2>
<p>ServiceNow provides a comprehensive, built-in help system directly within your instance, making it easy to find guidance without leaving the platform. Instance Help is one of the most underutilized features by new administrators, yet it's one of the most valuable tools for accelerating your learning and solving problems in real-time.</p>

<h3>Accessing Instance Help</h3>
<p>Instance Help is accessible through the <strong>question mark icon (?)</strong> in the banner frame at the top of every page. Clicking this icon opens the Help panel, which slides in from the right side of the screen without disrupting your current work.</p>

<h3>Help Panel Features</h3>
<p>The Help panel offers several types of assistance:</p>

<p><strong>1. Guided Tours</strong></p>
<p>Guided tours are interactive, step-by-step walkthroughs that highlight features and demonstrate workflows directly in your instance. They use visual indicators (highlights, tooltips, and arrows) to guide you through specific processes. Tours are available for:</p>
<ul>
<li>First-time login orientation</li>
<li>Feature introductions after upgrades</li>
<li>New application onboarding</li>
<li>Process walkthroughs (e.g., creating your first incident, setting up a knowledge base)</li>
</ul>

<p><strong>2. Contextual Help</strong></p>
<p>One of the most powerful features of Instance Help is its contextual awareness. The help content automatically adapts based on your current location in the platform. If you're on the Incident form, the help panel will show relevant articles about incident management, form fields, and related processes. This contextual intelligence saves time by surfacing the most relevant information without manual searching.</p>

<p><strong>3. Search Functionality</strong></p>
<p>The help panel includes a robust search engine that searches across:</p>
<ul>
<li>Official ServiceNow product documentation</li>
<li>Knowledge base articles within your instance</li>
<li>Community-contributed content and best practices</li>
<li>Release notes and known issues</li>
</ul>

<p><strong>4. Product Documentation Links</strong></p>
<p>Direct links to the official ServiceNow documentation (docs.servicenow.com) are provided throughout the help panel. The documentation is organized by product, feature, and release version, making it easy to find detailed technical reference information.</p>

<h3>Configuring Instance Help (For Administrators)</h3>
<p>As an administrator, you have the ability to customize the Instance Help experience for your organization:</p>
<ul>
<li><strong>Custom Guided Tours:</strong> Create organization-specific guided tours to help users navigate custom applications, modified processes, or new features specific to your instance. Tours can be created using the Guided Tour Designer, which provides a visual tool for defining tour steps, triggers, and conditions.</li>
<li><strong>Custom Help Content:</strong> Add organization-specific help articles that appear alongside ServiceNow's default content. This is useful for documenting custom configurations, company-specific processes, or internal policies.</li>
<li><strong>Embedded Help:</strong> Configure contextual help panels that appear automatically when users access specific forms or modules. This proactive assistance reduces support tickets and improves user adoption.</li>
<li><strong>Help Role Configuration:</strong> Control which help content is visible to different user roles. End users might see simplified guides, while administrators see detailed technical documentation.</li>
</ul>

<h3>Additional Help Resources Beyond Instance Help</h3>
<p>While Instance Help is your in-platform resource, ServiceNow provides an extensive ecosystem of learning resources:</p>
<ul>
<li><strong>Now Learning (nowlearning.servicenow.com):</strong> Official training platform with instructor-led and self-paced courses, learning paths, and certifications</li>
<li><strong>Community (community.servicenow.com):</strong> Forums, blogs, and user groups where administrators share knowledge, ask questions, and collaborate on solutions</li>
<li><strong>Developer Site (developer.servicenow.com):</strong> Technical resources including API documentation, code samples, PDI access, and developer tools</li>
<li><strong>ServiceNow Share:</strong> A marketplace of free, community-contributed applications, widgets, and components that you can install in your instance</li>
<li><strong>YouTube ServiceNow Channel:</strong> Video tutorials, product demos, and webinar recordings</li>
</ul>

<h3>Best Practice</h3>
<p>Make Instance Help a daily habit. Before searching external resources, check the help panel first — the contextual awareness often surfaces the exact information you need, saving significant time. Encourage your end users to explore Instance Help as well, as it can dramatically reduce the number of basic support requests your team receives.</p>`
      },
      {
        id: 'w-next-exp-task',
        title: 'Next Experience, Navigation, and Access Task',
        readingTime: 7,
        content: `<h2>Hands-On: Navigating the Next Experience</h2>
<p>In this practical exercise, you will explore the Next Experience interface and practice using key navigation features. These exercises should be performed in your Personal Developer Instance (PDI) to build hands-on familiarity with the platform.</p>

<h3>Exercise 1: Explore the All Menu</h3>
<ol>
<li>Log into your ServiceNow instance and locate the <strong>"All"</strong> button in the top-left of the banner frame</li>
<li>Click "All" to open the application menu. Notice the organized categories (Self-Service, Incident, Problem, Change, etc.)</li>
<li>In the filter field at the top of the All menu, type <strong>"incident"</strong> — observe how the menu instantly filters to show only Incident-related modules</li>
<li>Click <strong>"Incidents > All"</strong> to open the incident list view</li>
<li>Return to the All menu and type <strong>"user admin"</strong> to find User Administration modules</li>
<li>Navigate to <strong>"User Administration > Users"</strong> to view the user list</li>
</ol>

<h3>Exercise 2: Set Up Favorites</h3>
<ol>
<li>Navigate to <strong>Incidents > All</strong> from the All menu</li>
<li>Look for the <strong>star icon</strong> next to the page title or in the All menu next to the module name</li>
<li>Click the star to add "All Incidents" to your favorites</li>
<li>Repeat for at least 3 more modules you anticipate using frequently: Changes > All, Problem > All, and System Properties > All</li>
<li>Click the <strong>Favorites</strong> icon (star) in the banner frame to verify all your favorites appear</li>
<li>Try organizing your favorites by clicking "Edit Favorites" and creating a group called "Admin Tools"</li>
</ol>

<h3>Exercise 3: Check History</h3>
<ol>
<li>Navigate to several different modules in sequence: Incidents, Changes, Problems, Users, Knowledge</li>
<li>Click the <strong>History icon</strong> (clock) in the banner frame</li>
<li>Observe your navigation trail — all recently visited pages appear in chronological order</li>
<li>Click any item in the history to jump directly back to that page</li>
<li>Notice that history persists across sessions, so you can find pages you visited in previous login sessions</li>
</ol>

<h3>Exercise 4: Personalize Settings</h3>
<ol>
<li>Click your <strong>user avatar</strong> in the top-right corner of the banner frame</li>
<li>Select <strong>"Preferences"</strong> from the dropdown menu</li>
<li>Review and configure the following settings:
  <ul>
    <li><strong>Time Zone:</strong> Set to your local time zone</li>
    <li><strong>Date Format:</strong> Choose your preferred format</li>
    <li><strong>Rows per page:</strong> Set how many records you want to see in list views (20-50 is typical for admins)</li>
    <li><strong>Notification preferences:</strong> Configure which notifications you want to receive</li>
  </ul>
</li>
<li>Save your preferences and verify they take effect by checking a date field on any record</li>
</ol>

<h3>Exercise 5: Explore Workspaces</h3>
<ol>
<li>Click the <strong>Workspaces icon</strong> (grid) in the banner frame</li>
<li>Open the <strong>ITSM Workspace</strong> (or any available workspace)</li>
<li>Explore the workspace components: dashboard widgets, list views, and quick actions</li>
<li>Try opening a record from the workspace and notice how it opens in a split view or tab rather than navigating away</li>
<li>Notice how the workspace provides a focused environment designed for a specific role</li>
</ol>

<h3>Exercise 6: Global Search</h3>
<ol>
<li>Click the <strong>search icon</strong> (magnifying glass) in the banner or press <code>Ctrl+Shift+F</code></li>
<li>Type "password reset" and press Enter</li>
<li>Observe the search results — they include records from multiple tables (incidents, knowledge articles, catalog items, etc.)</li>
<li>Filter results by table type using the facets on the left side</li>
<li>Try searching for a specific incident number (e.g., INC0000001) to jump directly to that record</li>
</ol>

<h3>Key Takeaways</h3>
<p>The Next Experience interface is designed for efficiency and productivity. The three most important navigation habits to develop are: (1) use the All menu's type-ahead filter instead of browsing through categories, (2) add frequently used modules to Favorites immediately, and (3) learn keyboard shortcuts for common actions. Mastering these navigation fundamentals will save you significant time as you progress through more complex administration tasks.</p>`
      }
    ]
  },
  {
    id: 'w-lists',
    title: 'Lists and Filters',
    readingTime: 22,
    subtopics: [
      {
        id: 'w-lists-lesson',
        title: 'Lists and Filters Lesson',
        readingTime: 12,
        content: `<h2>Working with Lists and Filters</h2>
<p>Lists are the primary mechanism for viewing collections of records in ServiceNow. Every table in the platform can be displayed as a list view, and understanding how to work with lists, filters, and list configurations is one of the most fundamental skills for any ServiceNow administrator. You will spend a significant portion of your daily work in list views, so mastering these tools directly impacts your productivity.</p>

<h3>Understanding List Views</h3>
<p>A list view displays records from a table in a tabular format, similar to a spreadsheet. Each row represents a record, and each column represents a field on that record. List views are the starting point for most administrative tasks — viewing incidents, managing users, reviewing changes, and more.</p>

<h3>List View Components</h3>
<p>Every list view consists of these key components:</p>

<p><strong>1. Title Bar</strong></p>
<p>The title bar at the top of the list shows the table name, the number of records matching the current filter, and the page number. For example: "Incidents (234)" tells you there are 234 incidents matching your current filter criteria.</p>

<p><strong>2. Column Headers</strong></p>
<p>Column headers serve multiple purposes beyond just labeling the data:</p>
<ul>
<li><strong>Sorting:</strong> Click a column header once to sort ascending (A-Z, 0-9), click again for descending. A small arrow icon indicates the current sort direction.</li>
<li><strong>Context Menu:</strong> Right-click any column header to access powerful options including: Sort ascending/descending, Group By (organizes records into collapsible groups based on that field's values), Configure > List Layout (to add/remove columns), Bar Chart/Pie Chart (quick visualizations), and Export options.</li>
<li><strong>Column Resizing:</strong> Drag the edge of a column header to resize it. Double-click the edge to auto-fit the column width to its content.</li>
</ul>

<p><strong>3. Filter Breadcrumbs</strong></p>
<p>Below the title bar, breadcrumbs show all currently active filter conditions. Each condition appears as a separate element that can be individually removed. This visual representation helps you understand exactly what data you're viewing and makes it easy to modify filters incrementally.</p>

<p><strong>4. List Controls (Toolbar)</strong></p>
<p>The toolbar above the list provides action buttons:</p>
<ul>
<li><strong>New:</strong> Create a new record in this table</li>
<li><strong>Actions on selected rows:</strong> After checking boxes next to records, use the "Actions on selected rows" dropdown for bulk operations</li>
<li><strong>List context menu (hamburger icon):</strong> Access list-level settings, export options, and refresh</li>
</ul>

<p><strong>5. Record Rows</strong></p>
<p>Each row displays a record with its field values. Key behaviors:</p>
<ul>
<li>Click any field value to open the record in its form view</li>
<li>Right-click a row to access the record context menu with quick actions (Assign to me, Copy, Delete, Export this record, etc.)</li>
<li>Checkbox on the left allows selecting multiple records for bulk operations</li>
<li>The "i" icon (when hovering) shows a preview of the record without opening it</li>
</ul>

<h3>Building Filters</h3>
<p>Filters are the most powerful tool for finding specific records. ServiceNow provides both simple and advanced filtering methods:</p>

<p><strong>Condition Builder</strong></p>
<p>Click the filter icon (funnel) or the "Show Filter" option to open the condition builder above the list. Each condition consists of three parts:</p>
<ul>
<li><strong>Field:</strong> Which field to filter on (e.g., Priority, State, Assigned to)</li>
<li><strong>Operator:</strong> The comparison logic (is, is not, contains, starts with, is empty, greater than, less than, between, etc.)</li>
<li><strong>Value:</strong> The value to compare against (e.g., 1 - Critical, Active, specific user name)</li>
</ul>

<p><strong>Combining Conditions</strong></p>
<p>Multiple conditions can be combined using:</p>
<ul>
<li><strong>AND:</strong> All conditions must be true (intersection) — this is the default when adding conditions with "Add Filter Condition"</li>
<li><strong>OR:</strong> At least one condition must be true (union) — click "Add OR Condition" to add an alternative condition</li>
</ul>
<p>Complex filters can combine multiple AND and OR groups. For example: "(Priority is Critical AND State is Open) OR (Priority is High AND Assigned to is empty)" — this finds all critical open incidents as well as all unassigned high-priority incidents.</p>

<p><strong>Dot-Walking in Filters</strong></p>
<p>One of ServiceNow's most powerful filtering features is <strong>dot-walking</strong>, which allows you to filter based on fields in related tables. For example, on the Incident table, you can filter on <code>assigned_to.department</code> to find incidents assigned to users in a specific department, or <code>caller_id.location.city</code> to find incidents from callers in a specific city. Dot-walking follows reference field relationships between tables.</p>

<h3>Saving and Sharing Filters</h3>
<p>Frequently used filters can be saved for quick reuse:</p>
<ul>
<li><strong>Personal Filters:</strong> Saved filters visible only to you. Access these from the filter breadcrumb area.</li>
<li><strong>Global Filters:</strong> Filters shared with all users (requires admin role to create). Useful for standard views that all agents need.</li>
<li><strong>Encoded Queries:</strong> Every filter can be represented as an encoded query string (e.g., <code>priority=1^state=1</code>). These can be shared via URL, used in scripts, and embedded in links.</li>
</ul>

<h3>List Inline Editing</h3>
<p>When enabled, list inline editing allows you to double-click a field value in the list view to edit it directly without opening the record form. This is extremely efficient for bulk updates — for example, changing the assignment group on multiple incidents. However, be cautious: inline edits trigger the same business rules and server-side validation as form edits, so ensure you understand the implications of each change.</p>

<h3>Key Keyboard Shortcuts for Lists</h3>
<ul>
<li><code>Ctrl+Shift+F</code> — Focus the global search</li>
<li><code>j / k</code> — Navigate up/down in list rows (when enabled)</li>
<li><code>Ctrl+Enter</code> — Open selected record</li>
</ul>`
      },
      {
        id: 'w-lists-task',
        title: 'Lists and Filters Task',
        readingTime: 10,
        content: `<h2>Hands-On: Lists and Filters</h2>
<p>This hands-on exercise will give you practical experience with list views, filter construction, list configuration, and data export. Perform these exercises in your Personal Developer Instance (PDI).</p>

<h3>Exercise 1: Basic List Navigation</h3>
<ol>
<li>Navigate to <strong>Incidents > All</strong> to open the incident list</li>
<li>Observe the list structure: title bar showing record count, column headers, and the data rows</li>
<li>Click the <strong>"Number"</strong> column header to sort incidents by number ascending. Click again to reverse the sort order.</li>
<li>Right-click the <strong>"Priority"</strong> column header and select <strong>"Group By"</strong>. Observe how records are now organized into collapsible groups by priority level.</li>
<li>To remove grouping, right-click the column header again and select <strong>"Ungroup"</strong></li>
<li>Hover over any record row and look for the <strong>"i" (info)</strong> icon to preview the record without opening it</li>
</ol>

<h3>Exercise 2: Building Filters</h3>
<ol>
<li>On the incident list, click the <strong>filter icon (funnel)</strong> or click "Show Filter" to open the condition builder</li>
<li>Build this filter: <strong>Priority is 1 - Critical</strong>
  <ul>
    <li>Field: Priority</li>
    <li>Operator: is</li>
    <li>Value: 1 - Critical</li>
  </ul>
</li>
<li>Click <strong>"Run"</strong> to apply the filter. Note how the breadcrumb updates to show your active filter and the record count changes.</li>
<li>Add another condition: click <strong>"Add Filter Condition"</strong> (AND)
  <ul>
    <li>Field: State</li>
    <li>Operator: is</li>
    <li>Value: New</li>
  </ul>
</li>
<li>Run the filter again. You now see only Critical + New incidents. Note the breadcrumb shows "Priority is 1 - Critical AND State is New"</li>
<li>Now add an <strong>OR condition</strong>: click "Add OR Condition"
  <ul>
    <li>Field: Priority</li>
    <li>Operator: is</li>
    <li>Value: 2 - High</li>
  </ul>
</li>
<li>Run the filter. You now see Critical+New incidents OR High priority incidents.</li>
</ol>

<h3>Exercise 3: Dot-Walking Filters</h3>
<ol>
<li>Clear your existing filters by clicking the "X" on the breadcrumbs</li>
<li>Open the filter builder and set the field to <strong>"Assigned to"</strong></li>
<li>Notice that after selecting "Assigned to," you can continue to another field by clicking the small arrow — this is <strong>dot-walking</strong></li>
<li>Dot-walk to <strong>Assigned to > Department > Name</strong></li>
<li>Set the filter: Assigned to.Department.Name contains "IT"</li>
<li>Run the filter to see all incidents assigned to users in departments containing "IT"</li>
</ol>

<h3>Exercise 4: Configure List Layout</h3>
<ol>
<li>Right-click any column header and select <strong>"Configure > List Layout"</strong></li>
<li>The list layout dialog shows two panels: Available fields (left) and Selected fields (right)</li>
<li>Add the <strong>"Category"</strong> field by selecting it on the left and clicking the right arrow to move it to Selected</li>
<li>Remove the <strong>"Updated"</strong> field by selecting it on the right and clicking the left arrow</li>
<li>Reorder columns by selecting a field in the Selected panel and using the up/down arrows</li>
<li>Click <strong>"Save"</strong> to apply your new layout</li>
<li>Verify the list now shows your configured columns</li>
</ol>

<h3>Exercise 5: Saving Filters and Exporting Data</h3>
<ol>
<li>Build a useful filter (e.g., Priority = 1 - Critical AND State != Closed)</li>
<li>In the breadcrumb area, click <strong>"Save Filter"</strong></li>
<li>Name it "Critical Open Incidents" and save</li>
<li>Clear the filter, then verify you can re-apply it by selecting it from the saved filters dropdown</li>
<li>With the filter applied, right-click the list header and select <strong>"Export > CSV"</strong> to export the filtered data</li>
<li>Open the CSV file to verify the exported data matches your filtered list</li>
<li>Also try <strong>"Export > PDF"</strong> for a formatted report output</li>
</ol>

<h3>Exercise 6: Encoded Queries</h3>
<ol>
<li>Apply a filter to the incident list</li>
<li>Look at the URL in your browser — you'll see a parameter like <code>sysparm_query=priority=1^state!=7</code></li>
<li>Copy this URL and open it in a new tab — the same filtered view appears</li>
<li>This encoded query can be used in scripts: <code>gr.addEncodedQuery('priority=1^state!=7')</code></li>
<li>You can also right-click the breadcrumb and select "Copy query" to get the encoded query string directly</li>
</ol>

<h3>Key Takeaways</h3>
<p>Lists and filters are your primary tools for data discovery and management in ServiceNow. The three most important techniques to master are: (1) using the condition builder to construct precise multi-condition filters, (2) dot-walking to filter on related table fields, and (3) saving frequently-used filters for quick access. Spend time exploring these features — the efficiency gains compound significantly over time.</p>`
      }
    ]
  },
  {
    id: 'w-forms',
    title: 'Forms',
    readingTime: 22,
    subtopics: [
      {
        id: 'w-forms-lesson',
        title: 'Forms Lesson',
        readingTime: 12,
        content: `<h2>Understanding Forms in ServiceNow</h2>
<p>Forms are the primary interface for viewing, creating, and editing individual records in ServiceNow. Every record in the platform — whether it's an incident, a user profile, a change request, or a configuration item — is displayed and managed through a form. As an administrator, you will configure forms to meet your organization's specific needs, control how users interact with data, and ensure that critical information is captured consistently.</p>

<h3>Anatomy of a ServiceNow Form</h3>
<p>Every form in ServiceNow is composed of several key sections. Understanding each section is essential for effective administration:</p>

<p><strong>1. Form Header</strong></p>
<p>The form header appears at the top and contains:</p>
<ul>
<li><strong>Record Number:</strong> The unique identifier (e.g., INC0010045) that identifies this specific record</li>
<li><strong>State/Status Indicator:</strong> A visual indicator showing the current state of the record (e.g., New, In Progress, Resolved)</li>
<li><strong>Action Buttons:</strong> Save, Update, Submit, and other action buttons that trigger workflows and state transitions</li>
<li><strong>Additional Actions Menu:</strong> A dropdown with context-specific actions like "Resolve," "Close," "Copy," or custom actions</li>
<li><strong>Tags:</strong> Optional labels that can be added to records for categorization and quick identification</li>
</ul>

<p><strong>2. Form Body — Field Sections</strong></p>
<p>The main body of the form is organized into sections, each containing related fields. The default layout typically includes:</p>
<ul>
<li><strong>Primary Section:</strong> Contains the most important fields (Short Description, Priority, State, Assignment Group, Assigned To)</li>
<li><strong>Detail Sections:</strong> Additional sections organized by category (e.g., "Notes," "Resolution Information," "Closure Information")</li>
<li><strong>Custom Sections:</strong> Sections added by administrators for organization-specific fields</li>
</ul>

<p><strong>3. Field Types</strong></p>
<p>ServiceNow supports a wide variety of field types, each with specific behaviors for input, validation, display, and storage:</p>
<ul>
<li><strong>String:</strong> Single-line text input for short text values (up to 255 characters by default)</li>
<li><strong>Text:</strong> Multi-line text area for longer descriptions</li>
<li><strong>Integer / Decimal / Float:</strong> Numeric fields with specific precision requirements</li>
<li><strong>Boolean (True/False):</strong> Checkbox fields for yes/no values</li>
<li><strong>Choice:</strong> Dropdown select fields with predefined options</li>
<li><strong>Reference:</strong> Lookup fields that link to records in other tables (e.g., "Assigned to" references the User table). Reference fields include type-ahead search and a lookup icon for browsing.</li>
<li><strong>Date / Date-Time:</strong> Date picker fields with calendar widget</li>
<li><strong>HTML:</strong> Rich text editor for formatted content (used in knowledge articles)</li>
<li><strong>Journal (Work Notes / Additional Comments):</strong> Append-only text fields that maintain a timestamped history. Work Notes are internal (visible only to ITIL users), while Additional Comments are customer-visible.</li>
<li><strong>URL:</strong> Fields specifically for web addresses, rendered as clickable links</li>
<li><strong>Conditions:</strong> Fields that store filter conditions (used in Business Rules, UI Policies)</li>
<li><strong>Script:</strong> Code editor fields for storing JavaScript</li>
</ul>

<p><strong>4. Related Lists</strong></p>
<p>Below the form body, related lists show records from other tables that are associated with the current record. For example, an Incident form might show related Child Incidents, Associated CMDB Configuration Items, SLA records, and Activity History. Related lists provide a complete view of everything connected to a record.</p>

<p><strong>5. Activity Stream / Formatter</strong></p>
<p>The activity stream is a chronological timeline showing all changes, comments, work notes, and state transitions that have occurred on the record. This is critical for audit trails, collaboration, and understanding the history of a record. Each entry shows who made the change, when, and what was changed.</p>

<h3>Form Configuration for Administrators</h3>
<p>One of the most common administrator tasks is configuring form layouts. ServiceNow provides several tools for this:</p>

<p><strong>Form Designer</strong></p>
<p>The Form Designer is a drag-and-drop visual tool accessed via <strong>System UI > Form Designer</strong> or by right-clicking the form header and selecting "Configure > Form Design." With Form Designer, you can:</p>
<ul>
<li>Add, remove, and reorder fields on the form</li>
<li>Create multi-column layouts within sections</li>
<li>Add section headers and separators</li>
<li>Configure field properties (mandatory, read-only, visible)</li>
<li>Add annotations (instructional text) to guide users</li>
</ul>

<p><strong>Form Views</strong></p>
<p>Forms can have multiple views, each showing a different set of fields and layout. Views are identified by name (e.g., "Default," "Self Service," "Mobile"). The view used depends on the user's role, the access method, or the specific configuration. This allows you to show simplified forms to end users while providing comprehensive forms to agents and administrators.</p>

<h3>Controlling Form Behavior</h3>
<p>Beyond layout, administrators can control form behavior using several tools:</p>
<ul>
<li><strong>UI Policies:</strong> Declarative (no-code) rules that make fields mandatory, read-only, or visible/hidden based on conditions. For example, making "Resolution Notes" mandatory when the state changes to "Resolved."</li>
<li><strong>Client Scripts:</strong> JavaScript code that runs in the user's browser for dynamic form behavior — field validation, auto-population of related fields, dynamic field modifications, and custom alerts.</li>
<li><strong>Data Policies:</strong> Server-side rules that enforce data integrity regardless of how a record is created or modified (form, API, import, workflow).</li>
<li><strong>UI Actions:</strong> Custom buttons, links, and context menu items that appear on forms to trigger specific actions.</li>
</ul>`
      },
      {
        id: 'w-forms-task',
        title: 'Forms Task',
        readingTime: 10,
        content: `<h2>Hands-On: Working with Forms</h2>
<p>This hands-on exercise will give you practical experience with viewing records on forms, editing fields, configuring form layouts, and understanding the relationship between forms and the underlying data model.</p>

<h3>Exercise 1: Explore a Form's Anatomy</h3>
<ol>
<li>Navigate to <strong>Incidents > All</strong> and open any existing incident by clicking its Number</li>
<li>Identify each component of the form:
  <ul>
    <li><strong>Header:</strong> Find the incident number, state indicator, and action buttons (Save, Update)</li>
    <li><strong>Fields:</strong> Locate the Short Description, Priority, State, Category, Assigned to, and Assignment group fields</li>
    <li><strong>Sections:</strong> Scroll down to find different sections (Notes, Resolution Information, Related Records)</li>
    <li><strong>Related Lists:</strong> Scroll to the bottom to find related lists such as Child Incidents, SLAs, and Activity</li>
    <li><strong>Activity Stream:</strong> Find the timeline showing all changes made to this incident</li>
  </ul>
</li>
<li>Right-click the form header and select <strong>"Show XML"</strong> to see the raw data representation of the record. This helps understand how ServiceNow stores data internally.</li>
</ol>

<h3>Exercise 2: Edit Fields and Understand Field Types</h3>
<ol>
<li>On the same incident, modify the <strong>Short Description</strong> field — notice this is a String field with auto-save indicator</li>
<li>Change the <strong>Priority</strong> field — notice this is a Choice field with a dropdown of predefined options</li>
<li>Click the <strong>"Assigned to"</strong> field — notice this is a Reference field with type-ahead search. Start typing a name to see matching users. Click the lookup icon (magnifying glass) to browse the User table.</li>
<li>Scroll to the <strong>Work Notes</strong> field — notice this is a Journal field. Type a work note and click "Post." Observe that the note appears in the Activity Stream with your name and timestamp.</li>
<li>Now post an <strong>"Additional Comment"</strong> — note how this is differentiated from Work Notes in the activity stream. Work Notes are internal (green background), while Additional Comments are customer-visible.</li>
<li>Click <strong>"Update"</strong> to save all your changes</li>
</ol>

<h3>Exercise 3: Configure Form Layout</h3>
<ol>
<li>Right-click the form header and select <strong>"Configure > Form Layout"</strong></li>
<li>The Form Layout dialog shows two columns: Available fields (left) and Selected fields (right)</li>
<li>Add the <strong>"Subcategory"</strong> field: find it in Available, select it, and click the right arrow</li>
<li>Position it below the "Category" field using the up/down arrows in the Selected panel</li>
<li>Remove a field you don't need (e.g., "Watch list") by selecting it and clicking the left arrow</li>
<li>Click <strong>"Save"</strong> and verify the form now shows your modified layout</li>
<li>Note: Layout changes apply to the current view for all users with similar roles. Be thoughtful about changes in production.</li>
</ol>

<h3>Exercise 4: Use Form Designer</h3>
<ol>
<li>Right-click the form header and select <strong>"Configure > Form Design"</strong> to open the Form Designer</li>
<li>Observe the visual representation of the form with draggable field elements</li>
<li>Try dragging a field from the field palette on the left to a specific position on the form</li>
<li>Create a new <strong>Section</strong> by dragging a "Section" element onto the form. Name it "Custom Information"</li>
<li>Drag fields into your new section to organize them</li>
<li>Use the properties panel on the right to set field attributes (mandatory, read-only, etc.)</li>
<li>Click <strong>"Save"</strong> to apply your changes</li>
</ol>

<h3>Exercise 5: Create a New Record</h3>
<ol>
<li>Navigate to <strong>Incidents > Create New</strong> or click "New" from the incident list</li>
<li>Fill in the required fields:
  <ul>
    <li><strong>Caller:</strong> Select a user (this is who reported the incident)</li>
    <li><strong>Short Description:</strong> "Test Incident - Form Exercise"</li>
    <li><strong>Category:</strong> Select "Software"</li>
    <li><strong>Priority:</strong> Select "3 - Moderate"</li>
    <li><strong>Assignment group:</strong> Select an available group</li>
  </ul>
</li>
<li>Click <strong>"Submit"</strong> to create the record</li>
<li>Note the incident number that was automatically generated</li>
<li>Search for your incident using the global search to verify it was created</li>
</ol>

<h3>Key Takeaways</h3>
<p>Forms are the workhorses of ServiceNow — they're where users interact with data daily. As an administrator, the key skills to develop are: (1) understanding all field types and their behaviors, (2) configuring form layouts that are intuitive and efficient for users, (3) understanding the difference between Work Notes (internal) and Additional Comments (customer-visible), and (4) recognizing that form changes can be scoped to specific views and roles for targeted configuration.</p>`
      }
    ]
  },
  {
    id: 'w-tasks',
    title: 'Tasks',
    readingTime: 18,
    subtopics: [
      {
        id: 'w-tasks-lesson',
        title: 'Tasks Lesson',
        readingTime: 10,
        content: `<h2>Task Management in ServiceNow</h2>
<p>The Task table is arguably the most important table in the entire ServiceNow platform. It serves as the <strong>parent table</strong> (base class) for nearly all work-tracking record types, including Incidents, Problems, Changes, Requests, Stories, and many more. Understanding the task framework is crucial because every concept you learn about tasks applies to all of its child tables.</p>

<h3>The Task Table Hierarchy</h3>
<p>ServiceNow uses <strong>table inheritance</strong> — a database design pattern where child tables automatically inherit all fields, configurations, and behaviors from their parent table. The Task table (<code>task</code>) defines common fields that every work-tracking record needs, and its child tables extend it with type-specific fields.</p>

<p>Here is the Task table hierarchy with the most commonly used child tables:</p>
<ul>
<li><strong>Task [task]</strong> — The parent table
  <ul>
    <li><strong>Incident [incident]</strong> — Unplanned interruption or quality reduction of an IT service</li>
    <li><strong>Problem [problem]</strong> — Root cause of one or more incidents</li>
    <li><strong>Change Request [change_request]</strong> — Formal request to modify IT infrastructure or services</li>
    <li><strong>Requested Item [sc_req_item]</strong> — Item requested through the service catalog</li>
    <li><strong>Catalog Task [sc_task]</strong> — Fulfillment task for catalog requests</li>
    <li><strong>Story [rm_story]</strong> — Agile development work item</li>
    <li><strong>HR Case [sn_hr_core_case]</strong> — Human resources case</li>
    <li><strong>Security Incident [sn_si_incident]</strong> — Security-related incident</li>
  </ul>
</li>
</ul>

<h3>Common Task Fields</h3>
<p>Because these fields are defined on the Task table, they exist on every child table automatically:</p>
<ul>
<li><strong>Number:</strong> Auto-generated unique identifier (e.g., INC0010001, CHG0000023, REQ0000456)</li>
<li><strong>State:</strong> Current lifecycle stage of the task (New, In Progress, On Hold, Resolved, Closed)</li>
<li><strong>Priority:</strong> Calculated urgency level (1 - Critical, 2 - High, 3 - Moderate, 4 - Low, 5 - Planning)</li>
<li><strong>Assigned to:</strong> Individual user responsible for working the task</li>
<li><strong>Assignment group:</strong> Team responsible for the task</li>
<li><strong>Short description:</strong> Brief summary of the task</li>
<li><strong>Description:</strong> Detailed description with additional context</li>
<li><strong>Opened by / Opened at:</strong> Who created the task and when</li>
<li><strong>Updated by / Updated at:</strong> Last modification information</li>
<li><strong>Work notes / Additional comments:</strong> Journal fields for communication</li>
<li><strong>SLA due:</strong> When the task must be resolved per service level agreement</li>
<li><strong>Active:</strong> Whether the task is currently active or archived</li>
</ul>

<h3>Task States and Lifecycle</h3>
<p>Every task follows a lifecycle defined by its <strong>State</strong> field. While the specific states vary by task type, the general lifecycle pattern is:</p>
<ol>
<li><strong>New (1):</strong> Task has been created but work has not started</li>
<li><strong>In Progress (2):</strong> Work is actively being performed on the task</li>
<li><strong>On Hold (3):</strong> Work is temporarily paused, typically waiting for information or external action</li>
<li><strong>Resolved (6):</strong> The work has been completed and the task is awaiting confirmation</li>
<li><strong>Closed (7):</strong> The task is fully resolved and closed</li>
<li><strong>Cancelled (8):</strong> The task was cancelled without being completed</li>
</ol>
<p>State transitions can be controlled by <strong>Business Rules</strong> to enforce process compliance. For example, you might require that an incident cannot move to "Resolved" without resolution notes, or that a change request must go through approval before moving to "Implementation."</p>

<h3>Priority Calculation</h3>
<p>Priority in ServiceNow is typically calculated from two input fields:</p>
<ul>
<li><strong>Impact:</strong> How widely does this issue affect the organization? (1 - High, 2 - Medium, 3 - Low)</li>
<li><strong>Urgency:</strong> How quickly does this issue need to be resolved? (1 - High, 2 - Medium, 3 - Low)</li>
</ul>
<p>The platform uses a <strong>Priority Lookup Rules</strong> table to calculate Priority from the combination of Impact and Urgency. For example: High Impact + High Urgency = Priority 1 (Critical). This ensures consistent, objective prioritization across the organization.</p>

<h3>Assignment and Routing</h3>
<p>ServiceNow provides multiple mechanisms for routing tasks to the right people:</p>
<ul>
<li><strong>Manual Assignment:</strong> Agents or dispatchers manually set the Assignment group and Assigned to fields</li>
<li><strong>Assignment Rules:</strong> Automatic routing based on conditions (e.g., if Category = "Network" then assign to "Network Team")</li>
<li><strong>Data Lookup Rules:</strong> Automatically populate fields based on matching criteria</li>
<li><strong>Flow Designer / Workflow:</strong> Complex routing logic with approvals, escalations, and conditional branching</li>
<li><strong>Predictive Intelligence:</strong> AI-powered assignment that learns from historical data to suggest or automatically assign tasks to the most appropriate group or individual</li>
</ul>

<h3>Service Level Agreements (SLAs)</h3>
<p>SLAs define the expected response and resolution times for tasks based on their priority, category, or other criteria. ServiceNow automatically tracks SLA timers and can:</p>
<ul>
<li>Display countdown timers on task forms</li>
<li>Change visual indicators (red/yellow/green) based on SLA status</li>
<li>Trigger notifications when SLAs are about to breach</li>
<li>Escalate tasks (re-assign or notify managers) when SLAs are breached</li>
</ul>`
      },
      {
        id: 'w-tasks-task',
        title: 'Tasks - Practical Exercise',
        readingTime: 8,
        content: `<h2>Hands-On: Task Management</h2>
<p>This exercise explores the task framework, teaches you how different record types inherit from the Task table, and gives you practical experience with task lifecycle management.</p>

<h3>Exercise 1: Understand Table Inheritance</h3>
<ol>
<li>Navigate to <strong>System Definition > Tables</strong> and search for "task" in the filter</li>
<li>Open the <strong>Task [task]</strong> table record</li>
<li>Look for the <strong>"Extends table"</strong> field — Task extends no other table, making it a base table</li>
<li>Now search for <strong>"incident"</strong> in the tables list and open the <strong>Incident [incident]</strong> table record</li>
<li>Check the "Extends table" field — you'll see it extends <strong>Task [task]</strong></li>
<li>This inheritance means every field on Task automatically exists on Incident</li>
<li>Repeat this for Change Request [change_request] and Problem [problem] to confirm they also extend Task</li>
</ol>

<h3>Exercise 2: View All Tasks</h3>
<ol>
<li>In the URL bar, type <strong>task.list</strong> and press Enter to view all task-based records</li>
<li>Notice that this list shows Incidents, Problems, Changes, Requests, and all other task types mixed together</li>
<li>Use the filter to group by <strong>"Sys class name"</strong> (which indicates the specific task type) to see the distribution</li>
<li>This demonstrates how the Task table serves as a unified view of all work across the platform</li>
</ol>

<h3>Exercise 3: Create and Manage an Incident Through Its Lifecycle</h3>
<ol>
<li><strong>Create:</strong> Go to Incidents > Create New. Fill in:
  <ul>
    <li>Caller: Select any user</li>
    <li>Short Description: "Email client crashes when opening attachments"</li>
    <li>Category: Software</li>
    <li>Impact: 2 - Medium</li>
    <li>Urgency: 2 - Medium</li>
  </ul>
</li>
<li>Observe that <strong>Priority</strong> was automatically calculated from Impact + Urgency</li>
<li>Assign the incident to a group (e.g., "Software Support") and click Submit</li>
<li><strong>Work the Incident:</strong> Open the incident. Change State from "New" to "In Progress." Add a work note: "Investigating the reported issue. Attempting to reproduce."</li>
<li><strong>Resolve:</strong> Change State to "Resolved." Fill in the Resolution Code (e.g., "Solved (Permanently)") and Resolution Notes: "Updated email client to version 2.5.1 which fixes the attachment rendering bug." Click Update.</li>
<li><strong>Close:</strong> Change State to "Closed" (or wait for automatic closure per your SLA rules). Add a closing note: "Confirmed fix with the caller. Issue resolved."</li>
<li>Review the <strong>Activity Stream</strong> to see the complete history of state transitions, work notes, and changes</li>
</ol>

<h3>Exercise 4: Compare Different Task Types</h3>
<ol>
<li>Open a <strong>Change Request</strong> (Changes > All, open any record)</li>
<li>Compare the form with the Incident form — notice the shared fields (Number, State, Priority, Assigned to, Short Description) and the unique fields (Risk, Type, Change window)</li>
<li>Open a <strong>Problem</strong> (Problem > All) and compare again</li>
<li>Notice how each task type has the common Task fields plus its own specialized fields</li>
</ol>

<h3>Exercise 5: Explore SLA Tracking</h3>
<ol>
<li>Open an incident and scroll to the bottom to find the <strong>"Task SLAs"</strong> related list</li>
<li>If SLAs are configured, you'll see SLA timer records showing response and resolution targets</li>
<li>Each SLA shows its current status (In Progress, Achieved, Breached) and remaining time</li>
<li>Navigate to <strong>Service Level Management > SLA Definitions</strong> to view configured SLAs</li>
<li>Examine how SLA definitions specify: start condition, stop condition, target duration, and escalation actions</li>
</ol>

<h3>Key Takeaways</h3>
<p>The Task table hierarchy is the backbone of ServiceNow's process management. Key concepts to internalize: (1) table inheritance means changes to the Task table can affect all child tables, (2) Priority is calculated from Impact + Urgency using lookup rules, (3) state transitions can be controlled by business rules to enforce processes, (4) SLAs provide automated compliance tracking, and (5) the task framework's consistency makes it easy to apply lessons learned with one task type to all others.</p>`
      }
    ]
  },
];
